import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowRight, CheckCircle, Phone } from "@phosphor-icons/react";

// Lazy-loads the Calendly inline widget script/css once (only on this page).
function useCalendly() {
  useEffect(() => {
    const CSS_ID = "calendly-widget-css";
    const JS_ID = "calendly-widget-js";
    if (!document.getElementById(CSS_ID)) {
      const link = document.createElement("link");
      link.id = CSS_ID;
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }
    if (!document.getElementById(JS_ID)) {
      const script = document.createElement("script");
      script.id = JS_ID;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
}

export default function BookCall() {
  const [params] = useSearchParams();
  const initialPlan = params.get("plan") || "";
  const CALENDLY_URL = "https://calendly.com/georgeatabora/30min";
  useCalendly();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    plan_interest: initialPlan, best_time: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads/book-call", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="book-call-page">
      <Toaster theme="dark" />
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "var(--border)", background: "rgba(10,20,24,0.85)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-2xl tracking-tighter">
            MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD
          </Link>
          <Link to="/pricing" className="overline hover:text-white">← pricing</Link>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-12 md:py-20">
        {sent ? (
          <div className="text-center py-12">
            <CheckCircle size={72} weight="duotone" color="var(--accent)" className="mx-auto" />
            <div className="overline mt-6 mb-2" style={{ color: "var(--accent)" }}>// received</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">You're on the list.</h1>
            <p className="mt-6 max-w-lg mx-auto text-lg" style={{ color: "var(--text-2)" }}>
              Thanks {form.name.split(" ")[0]} — George will reach out to <strong style={{ color: "var(--text)" }}>{form.email}</strong> within 24 hours to schedule your call.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/" className="btn-secondary">Back to home</Link>
              <Link to="/register" className="btn-primary flex items-center gap-2">Start with Free →</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="overline mb-4">// book a discovery call</div>
            <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-none">
              Let's talk <span style={{ color: "var(--accent)" }}>scope</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-lg" style={{ color: "var(--text-2)" }}>
              Paid tiers are custom-scoped based on your team size, contact volume, campaign complexity, and integration needs. Book a quick 20-minute call and George will send you a tailored quote — no automated pricing games.
            </p>

            {/* Inline Calendly embed */}
            <div className="mt-10 border" style={{ borderColor: "var(--accent)" }} data-testid="calendly-embed-box">
              <div className="p-5 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.06)" }}>
                <div>
                  <div className="overline" style={{ color: "var(--accent)" }}>// pick a time — 30 min zoom</div>
                  <h3 className="font-display font-bold text-xl mt-1">Grab a slot on my calendar</h3>
                </div>
                <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="overline hover:text-white" data-testid="calendly-open-new-tab">open in new tab →</a>
              </div>
              <div
                className="calendly-inline-widget"
                data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=0F1E24&text_color=FFFFFF&primary_color=2DD4D4`}
                style={{ minWidth: "320px", height: "700px" }}
                data-testid="calendly-inline"
              />
            </div>

            <div className="my-10 flex items-center gap-4">
              <div className="flex-1 border-t" style={{ borderColor: "var(--border)" }}></div>
              <span className="overline">or send details first, book later</span>
              <div className="flex-1 border-t" style={{ borderColor: "var(--border)" }}></div>
            </div>

            <form onSubmit={submit} className="mt-2 border p-6 md:p-8" style={{ borderColor: "var(--border)" }} data-testid="book-call-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-base">Full name *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="book-name" />
                </div>
                <div>
                  <label className="label-base">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" data-testid="book-email" />
                </div>
                <div>
                  <label className="label-base">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-base" data-testid="book-phone" />
                </div>
                <div>
                  <label className="label-base">Company</label>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-base" data-testid="book-company" />
                </div>
                <div>
                  <label className="label-base">Plan of interest</label>
                  <select value={form.plan_interest} onChange={(e) => setForm({ ...form, plan_interest: e.target.value })} className="input-base"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="book-plan">
                    <option value="">— not sure yet —</option>
                    <option value="Starter">Starter</option>
                    <option value="Pro">Pro</option>
                    <option value="Team">Team</option>
                    <option value="Enterprise">Enterprise / custom</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Best time to call</label>
                  <input value={form.best_time} onChange={(e) => setForm({ ...form, best_time: e.target.value })} placeholder="e.g. Tues/Wed afternoons EST" className="input-base" data-testid="book-time" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-base">Tell me about your business (optional)</label>
                  <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Team size, monthly contact volume, current CRM, what problem you're trying to solve..." className="input-base"
                    style={{ border: "1px solid var(--border)", padding: "12px" }} data-testid="book-message" />
                </div>
              </div>
              <button type="submit" disabled={busy || !form.name || !form.email} data-testid="book-submit" className="btn-primary mt-8 w-full md:w-auto flex items-center justify-center gap-2">
                {busy ? "Sending..." : "Request my call"} <ArrowRight weight="bold" />
              </button>
              <p className="mt-4 text-xs" style={{ color: "var(--text-3)" }}>
                By submitting you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>. George will call/email you at the number/address you provided. No spam.
              </p>
            </form>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="border p-6" style={{ borderColor: "var(--border)" }}>
                <Phone weight="duotone" size={24} color="var(--accent)" />
                <h4 className="font-display font-bold mt-3 text-lg" style={{ color: "var(--text)" }}>20 minutes</h4>
                <p className="mt-2" style={{ color: "var(--text-2)" }}>Quick scoping call — no long sales pitch. We figure out fit.</p>
              </div>
              <div className="border p-6" style={{ borderColor: "var(--border)" }}>
                <ArrowRight weight="duotone" size={24} color="var(--accent)" />
                <h4 className="font-display font-bold mt-3 text-lg" style={{ color: "var(--text)" }}>Tailored quote</h4>
                <p className="mt-2" style={{ color: "var(--text-2)" }}>Setup + monthly pricing based on your team size and complexity.</p>
              </div>
              <div className="border p-6" style={{ borderColor: "var(--border)" }}>
                <CheckCircle weight="duotone" size={24} color="var(--accent)" />
                <h4 className="font-display font-bold mt-3 text-lg" style={{ color: "var(--text)" }}>Invoice direct</h4>
                <p className="mt-2" style={{ color: "var(--text-2)" }}>Once we agree, invoices are sent directly. No surprise auto-charges.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
